import { createFeature, createReducer, on } from '@ngrx/store';
import { interestWizardStepSubmitted } from '../actions/interest-wizard.actions';
import { StepId } from '../../services/interest-wizard.service';
import { logoutOAuth } from '../actions/session-auth.actions';
import {
  userProfileFetched,
  userProfileUpdated,
} from '../actions/session-profile.actions';
import { WidgetId } from '../selectors/widgets.selector';

type TrueFalse = 'true' | 'false';
export type InterestWizardState = 'NOT_STARTED' | 'FINISHED' | StepId;
export type UserOnboardingLevel = 'NONE' | 'BASIC' | 'FULL';

type NotificationsEmail = {
  enabled: TrueFalse | boolean;
  topics?: {
    messages: TrueFalse | boolean;
    appointments: TrueFalse | boolean;
    documents: TrueFalse | boolean;
    logbook: TrueFalse | boolean;
    transition: TrueFalse | boolean;
    cancellation: TrueFalse | boolean;
    questionnaires?: TrueFalse | boolean;
    authorizations: TrueFalse | boolean;
  };
};
type NotificationsPushData = {
  eventTypeName: string;
  subscription: TrueFalse | boolean;
};
export type NotificationsPush = {
  type: string;
  data: NotificationsPushData[];
};

type UserPersonalInfo = {
  titel?: string;
  firstname?: string;
  lastname?: string;
  birthdate?: string;
  mobile?: string;
  gender?: string;
  address?: {
    zip?: string;
    city?: string;
    street?: string;
    houseNumber?: string;
  };
};

export type UserProfileResource = {
  'user-profile.version': string;
  language: string | { preferred: string };
  'notifications.push': NotificationsPush;
  'notifications.email': NotificationsEmail;
  interests?: fhir4.Coding[]; // if undefined, then it is first time loading (before info popup was displayed)
  dashboard_configuration?: WidgetId[];
  personalInfo?: UserPersonalInfo;
  onboarding_level: UserOnboardingLevel;
  onboarding_consent?: boolean;
  interest_wizard_state: InterestWizardState;
  region: {
    code: string;
    zip: string;
  };
  partner?:
    | {
        code: string;
        display: string;
        system: string;
        logo_url: string;
      }
    | string;
};

export type UserNotifications = {
  notificationsEnabled?: boolean;
  notificationsTopicsMessages?: boolean;
  notificationsTopicsAppointments?: boolean;
  notificationsTopicsDocuments?: boolean;
  notificationsTopicsLogbook?: boolean;
  notificationsTopicsAuthorizations?: boolean;
  notificationsTopicsTransition?: boolean;
  notificationsTopicsCancellation?: boolean;
  notificationsTopicsQuestionnaires?: boolean;
};
export type UserPushNotifications = {
  notificationsPushMessages?: boolean;
  notificationsPushAppointments?: boolean;
  notificationsPushDocuments?: boolean;
  notificationsPushLogbook?: boolean;
  notificationsPushTransition?: boolean;
  notificationsPushCancellation?: boolean;
  notificationsPushQuestionnaires?: boolean;
  notificationsPushAuthorizations?: boolean;
};

type UserSettings = {
  language: string;
  dashboard_configuration?: WidgetId[];
};

type UserOnboarding = {
  onboarding_level?: UserOnboardingLevel; // todo enum
  onboarding_consent?: boolean;
  interest_wizard_state: InterestWizardState;
};

export type UserInterests = {
  ageGroup?: string;
  categories?: string[];
  themes?: string[];
  situation?: string;
};

export type UserProfile = {
  regionCode?: string;
  partnerCode?: string;
  regionZip?: string;
  partnerDisplay?: string;
  partnerLogoUrl?: string;
  personalInfo?: UserPersonalInfo;
  interests?: UserInterests;
} & UserOnboarding &
  UserNotifications &
  UserPushNotifications &
  UserSettings & { _resource: UserProfileResource };

export const SESSION_PROFILE_STATE_KEY = 'sessionProfile';

export type SessionProfileState = {
  userProfile: UserProfile | null;
};

const initialState: SessionProfileState = {
  userProfile: null,
};

const reducer = createReducer(
  initialState,
  on(logoutOAuth, () => initialState),
  on(userProfileFetched, userProfileUpdated, (state, { userProfile }) => ({
    ...state,
    userProfile,
  })),
  on(interestWizardStepSubmitted, (state, { payload }) => ({
    ...state,
    userProfile: {
      ...state.userProfile,
      ...payload,
    },
  })),
);

export const sessionProfileFeature = createFeature({
  name: SESSION_PROFILE_STATE_KEY,
  reducer,
});
